import { useState, useEffect } from 'react';
import { Star, Flower2 } from 'lucide-react';
import { usePlant } from '../../../context/PlantContext';
import { plantsApi } from '../../../services/api';
import CompactPlantCard from './CompactPlantCard';

/**
 * PlantFavoritesTab - Scrollable list of favorited plants
 *
 * Displays all favorited plants as compact cards with quick access
 * to placement and removal. Plants are sorted alphabetically by their
 * German name for better findability. Shows empty state when no favorites.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onOpenCatalog - Callback to open plant catalog (optional)
 */
const PlantFavoritesTab = ({ onOpenCatalog }) => {
  const {
    favorites,
    removeFromFavorites,
    enterPlacementMode,
    openCatalog
  } = usePlant();

  // State to store full plant objects fetched from API
  const [favoritePlants, setFavoritePlants] = useState([]);
  // Only "loading" when there are favorites to fetch — derive the initial value.
  const [isLoading, setIsLoading] = useState(() => favorites.length > 0);
  const [error, setError] = useState(null);
  const [prevFavCount, setPrevFavCount] = useState(favorites.length);

  // When the favorites list empties out, clear the stale state during render
  // rather than in the effect (avoids a synchronous setState-in-effect cascade).
  if (favorites.length !== prevFavCount) {
    setPrevFavCount(favorites.length);
    if (favorites.length === 0) {
      setFavoritePlants([]);
      setError(null);
      setIsLoading(false);
    }
  }

  // Fetch favorite plants data from API
  useEffect(() => {
    if (favorites.length === 0) return;

    const fetchFavoritePlants = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch all favorite plants from API
        const plants = await Promise.all(
          favorites.map(id => plantsApi.getById(id))
        );

        // Filter out any null/undefined results (in case API call fails for specific plant)
        const validPlants = plants.filter(plant => plant != null);

        // Sort alphabetically by german_name for better findability
        validPlants.sort((a, b) => {
          const nameA = a.german_name || a.name || '';
          const nameB = b.german_name || b.name || '';
          return nameA.localeCompare(nameB, 'de');
        });

        setFavoritePlants(validPlants);
      } catch (err) {
        console.error('Failed to fetch favorite plants:', err);
        setError('Fehler beim Laden der Favoriten');
        setFavoritePlants([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFavoritePlants();
  }, [favorites]);

  // Handle remove from favorites
  const handleRemove = (plantId) => {
    removeFromFavorites(plantId);
  };

  // Handle place plant
  const handlePlace = (plant) => {
    enterPlacementMode(plant);
  };

  // Handle open catalog
  const handleOpenCatalog = () => {
    if (onOpenCatalog) {
      onOpenCatalog();
    } else {
      openCatalog();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
          <p className="text-xs text-gray-500 mt-3">Lade Favoriten...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
            <span className="text-red-600 text-xl">!</span>
          </div>
          <h3 className="text-sm font-semibold text-gray-700 mb-1">
            {error}
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Bitte versuche es später erneut.
          </p>
          <button
            onClick={handleOpenCatalog}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            <Flower2 className="w-4 h-4" />
            Katalog öffnen
          </button>
        </div>
      </div>
    );
  }

  // Empty state - no favorites
  if (favoritePlants.length === 0) {
    return (
      <div className="p-4">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Star className="w-12 h-12 text-gray-400 mb-3" />
          <h3 className="text-sm font-semibold text-gray-700 mb-1">
            Keine Favoriten
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Füge Pflanzen zu deinen Favoriten hinzu,
            <br />
            um schnell darauf zugreifen zu können.
          </p>
          <button
            onClick={handleOpenCatalog}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            <Flower2 className="w-4 h-4" />
            Katalog öffnen
          </button>
        </div>
      </div>
    );
  }

  // Favorites list
  return (
    <div
      className="flex flex-col gap-2 p-4 overflow-y-auto"
      style={{ maxHeight: 'calc(100vh - 180px)' }}
    >
      {/* Header */}
      <div className="mb-2">
        <p className="text-xs text-gray-600">
          {favoritePlants.length} {favoritePlants.length === 1 ? 'Favorit' : 'Favoriten'}
        </p>
      </div>

      {/* Favorites Cards */}
      {favoritePlants.map(plant => (
        <CompactPlantCard
          key={plant.id}
          plant={plant}
          onRemove={handleRemove}
          onPlace={handlePlace}
        />
      ))}
    </div>
  );
};

export default PlantFavoritesTab;
